/**
 * Image loading utilities for the reviewer PDF report.
 *
 * - Loads the bundled CSV Pro brand logo via Vite asset import.
 * - Loads a remote client logo (e.g. a Supabase storage URL) and converts
 *   it to a data URL so jsPDF can embed it without CORS surprises.
 * - Returns the natural pixel dimensions so callers can preserve aspect
 *   ratio when rendering at a target visual height.
 */

// Bundled brand logo. Vite returns the resolved asset URL.
import csvProLogoUrl from "@/assets/csv-pro-logo.png";

export interface LoadedLogo {
  /** data: URL safe to pass to jsPDF.addImage(). */
  dataUrl: string;
  /** Native pixel width. */
  width: number;
  /** Native pixel height. */
  height: number;
  /** "PNG" | "JPEG" — best-effort for jsPDF format hint. */
  format: "PNG" | "JPEG";
}

/** Load the CSV Pro brand logo (always available). */
export async function loadCsvProLogo(): Promise<LoadedLogo | null> {
  return loadImageAsLogo(csvProLogoUrl, "csv pro logo");
}

/** Load a client logo from a public URL. Returns null on any failure.
 *  Logs the precise failure reason to aid debugging. */
export async function loadClientLogo(url?: string | null): Promise<LoadedLogo | null> {
  if (!url) {
    console.warn("[reviewer-pdf] client logo: no logo URL found");
    return null;
  }
  try {
    const logo = await loadImageAsLogo(url, "client logo");
    if (!logo) console.warn("[reviewer-pdf] client logo: loadImageAsLogo returned null", { url });
    return logo;
  } catch (e) {
    console.warn("[reviewer-pdf] client logo: unexpected error", e);
    return null;
  }
}

async function loadImageAsLogo(url: string, label: string): Promise<LoadedLogo | null> {
  let res: Response;
  try {
    res = await fetch(url, { mode: "cors" });
  } catch (e) {
    console.warn(`[reviewer-pdf] ${label}: URL not accessible`, { url, e });
    return null;
  }
  if (!res.ok) {
    console.warn(`[reviewer-pdf] ${label}: URL not accessible`, { url, status: res.status });
    return null;
  }
  console.info(`[reviewer-pdf] ${label}: fetch succeeds`, { url, status: res.status, contentType: res.headers.get("content-type") });
  const blob = await res.blob();
  if (blob.type.includes("text/html")) {
    console.warn(`[reviewer-pdf] ${label}: URL not accessible`, { url, reason: "Expected image but received HTML" });
    return null;
  }
  let dataUrl: string;
  try {
    dataUrl = await blobToDataUrl(blob);
    console.info(`[reviewer-pdf] ${label}: image converts to data URL`, { byteSize: blob.size, mimeType: blob.type || "unknown" });
  } catch (e) {
    console.warn(`[reviewer-pdf] ${label}: conversion to data URL failed`, e);
    return null;
  }
  let dims: { w: number; h: number };
  try {
    dims = await readImageDims(dataUrl);
  } catch (e) {
    console.warn(`[reviewer-pdf] ${label}: image decode failed`, e);
    return null;
  }
  const mime = blob.type || dataUrl.match(/^data:([^;]+)/)?.[1] || "";
  if (mime.includes("jpeg") || mime.includes("jpg")) {
    return { dataUrl, width: dims.w, height: dims.h, format: "JPEG" };
  }
  if (mime.includes("png")) {
    return { dataUrl, width: dims.w, height: dims.h, format: "PNG" };
  }

  try {
    dataUrl = await convertImageDataUrlToPng(dataUrl, dims);
    console.info(`[reviewer-pdf] ${label}: normalized unsupported image type to PNG`, { mimeType: mime || "unknown" });
  } catch (e) {
    console.warn(`[reviewer-pdf] ${label}: conversion to data URL failed`, { mimeType: mime || "unknown", e });
    return null;
  }
  const format: "PNG" | "JPEG" = "PNG";
  return { dataUrl, width: dims.w, height: dims.h, format };
}

function convertImageDataUrlToPng(dataUrl: string, dims: { w: number; h: number }): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = dims.w;
        canvas.height = dims.h;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("canvas context unavailable");
        ctx.drawImage(img, 0, 0, dims.w, dims.h);
        resolve(canvas.toDataURL("image/png"));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error("image decode failed during PNG normalization"));
    img.src = dataUrl;
  });
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function readImageDims(dataUrl: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => reject(new Error("image decode failed"));
    img.src = dataUrl;
  });
}

/**
 * Compute scaled width/height for a logo so that it fits within a bounding
 * box AND ideally hits a target visual height. Aspect ratio is preserved.
 *
 * Layout strategy:
 *   1. Scale to targetHeight first (visual weight equality).
 *   2. If width then exceeds maxWidth, rescale to maxWidth.
 */
export function fitLogo(
  logo: LoadedLogo,
  opts: { maxWidth: number; maxHeight: number; targetHeight?: number },
): { w: number; h: number } {
  const target = opts.targetHeight ?? opts.maxHeight;
  let h = Math.min(target, opts.maxHeight);
  let w = (logo.width / logo.height) * h;
  if (w > opts.maxWidth) {
    w = opts.maxWidth;
    h = (logo.height / logo.width) * w;
  }
  return { w, h };
}
